/**
 * A BIZONYLATTÍPUSOK SZELETE — a #25 harmadik szelete a felületen.
 *
 * ═══ MIÉRT MÁTRIX, ÉS NEM CÉGENKÉNTI PANEL ═══
 *
 * Viktor döntése artboardon (2026-09-13): minden cég egy sor, minden típus egy
 * oszlop. Két érv döntött. Egy: ~80 cégnél a „kapcsold be mindenkinél a
 * nyugtát" egy lefelé haladás, nem nyolcvan kijelölés. Kettő: a billentyűzet.
 * A cégenkénti panel a négy kapcsolót SZÁMOKRA kötötte volna (1–4), és a
 * számok elfogynak, ha a `DocType` egyszer bővül — a mátrixban ugyanez nyíl,
 * és a nyilak nem fogynak el.
 *
 * ═══ AMI ITT NINCS, ÉS EZ DÖNTÉS ═══
 *
 * ⚠️ NINCS MENTÉS GOMB. Egy kapcsoló, ami csak egy másik gomb megnyomása után
 * él, két állapotot mutat egyszerre (amit látsz, és ami érvényes). A kérés a
 * cég TELJES listáját viszi, tehát a kapcsolás önmagában befejezett művelet.
 *
 * ⚠️ NINCS MEGERŐSÍTŐ ABLAK. A művelet visszafordítható, a következménye pedig
 * OTT LÁTSZIK a soron: a pipa eltűnik. Az inaktiválásnál azért van ablak, mert
 * ott a következmény három olyan képernyőn jelenik meg, ami nincs a szem előtt.
 *
 * ⚠️ A BANKKIVONAT NEM OSZLOP. E-mailben jön a banktól (Viktor, 2026-09-11) —
 * az ügyfél sosem választja, tehát egy kapcsoló rá semmit nem változtatna.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { docTypeLabel } from '@/i18n/generated';
import { visibleCompanies } from './CompanySettings';
import { blockingView } from './companyLoadState';
import { type CompanyDocType, type CompanyRow, useCompanies } from './useCompanies';

/**
 * A NÉGY KAPCSOLHATÓ TÍPUS, a felület sorrendjében.
 *
 * Ugyanaz a négy, amit a szerver elfogad (`CLIENT_DECLARABLE_DOC_TYPES`), és a
 * sorrendjük is ugyanaz: a gyakoribb eset áll elöl. A szűkítés a SZERVEREN dől
 * el — ez itt az oszlopok sorrendje, nem a szabály.
 */
const TOGGLEABLE: readonly CompanyDocType[] = [
  'INBOUND_INVOICE',
  'OUTBOUND_INVOICE',
  'RECEIPT',
  'OTHER',
];

/** Nyíl → elmozdulás a mátrixban: [sor, oszlop]. */
const MOVES: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

/** A kapcsolás UTÁNI teljes lista — a `TOGGLEABLE` sorrendjében. */
export function toggledDocTypes(
  current: readonly CompanyDocType[],
  docType: CompanyDocType
): CompanyDocType[] {
  const on = new Set(current);
  if (on.has(docType)) {
    on.delete(docType);
  } else {
    on.add(docType);
  }
  // ⚠️ A `TOGGLEABLE`-ből építjük, nem a meglévő listából: így egy ismeretlen
  // vagy nem kapcsolható érték (`BANK_STATEMENT`) nem megy vissza a szervernek,
  // ami 400-zal utasítaná el — egy nyugta-kattintás miatt.
  return TOGGLEABLE.filter((type) => on.has(type));
}

export function DocTypeSettings() {
  const { t } = useTranslation();
  const state = useCompanies();
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  /** Melyik cég sora nyugtázza a mentést — a kapcsolásig ott marad. */
  const [savedId, setSavedId] = useState<string | null>(null);

  /**
   * A CELLÁK A NYILAKHOZ. A `Map` kulcsa `cégId:típus`; a `null` a React
   * leszereléskori hívása, azt is el kell fogadni.
   */
  const cells = useRef(new Map<string, HTMLInputElement | null>());

  const visible = useMemo(
    () => visibleCompanies(state.companies, query, showInactive),
    [state.companies, query, showInactive]
  );

  const moveFocus = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>, row: number, column: number) => {
      const move = MOVES[event.key];
      if (!move) {
        return;
      }
      // A NYÍL NE GÖRGESSEN. A táblázat magasabb a képernyőnél; a görgetés
      // elvinné a kurzort a szem elől, miközben a fókusz nem mozdul.
      event.preventDefault();

      const company = visible[row + move[0]];
      const docType = TOGGLEABLE[column + move[1]];
      if (company === undefined || docType === undefined) {
        return;
      }
      cells.current.get(`${company.id}:${docType}`)?.focus();
    },
    [visible]
  );

  const blocked = blockingView(state, t);
  if (blocked) {
    return blocked;
  }

  function toggle(company: CompanyRow, docType: CompanyDocType) {
    // ⚠️ A PÁRHUZAMOS ÍRÁST A HOROG ZÁRJA KI (`inFlight`), nem egy itteni
    // `status === 'saving'` ág: az ugyanazt a kérést kétszer őrizné, és a
    // `DocTypeSettings.spec` szabotázs-próbája ki is mutatta, hogy az itteni
    // ellenőrzés nélkül SEM megy el második kérés. Ami viszont ide tartozik:
    // a láb kimondja, hogy mentés van folyamatban — különben a kolléga csak
    // annyit látna, hogy a kattintása nem csinál semmit.
    setSavedId(null);
    state.setDocTypes(company.id, toggledDocTypes(company.docTypes, docType), () =>
      setSavedId(company.id)
    );
  }

  return (
    <>
      <div className="settings__toolbar">
        <input
          type="search"
          aria-label={t('companies.searchPlaceholder')}
          placeholder={t('companies.searchPlaceholder')}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <label className="settings__toggle">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(event) => setShowInactive(event.target.checked)}
          />
          {t('companies.showInactive')}
        </label>
      </div>

      {state.status === 'invalid' || state.status === 'failed' || state.status === 'conflict' ? (
        <p className="error" role="alert">
          {state.detail === '' ? t('docTypes.failed') : state.detail}
        </p>
      ) : null}

      {visible.length === 0 ? (
        <p className="empty">
          {t(state.companies.length === 0 ? 'companies.empty' : 'companies.noMatch')}
        </p>
      ) : (
        <table className="list">
          <thead>
            <tr>
              <th>{t('companies.column.name')}</th>
              <th>{t('companies.column.taxNumber')}</th>
              {TOGGLEABLE.map((docType) => (
                <th key={docType} className="docmatrix__head">
                  {docTypeLabel(t, docType)}
                </th>
              ))}
              <th>{/* a mentés nyugtája — nem adat, nincs fejléce */}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((company, row) => (
              <tr key={company.id} className={company.active ? 'row' : 'row row--inactive'}>
                <td>{company.name}</td>
                <td>{company.taxNumber}</td>
                {TOGGLEABLE.map((docType, column) => (
                  <td key={docType} className="docmatrix__cell">
                    <input
                      type="checkbox"
                      ref={(cell) => {
                        cells.current.set(`${company.id}:${docType}`, cell);
                      }}
                      checked={company.docTypes.includes(docType)}
                      aria-label={t('docTypes.toggle', {
                        company: company.name,
                        type: docTypeLabel(t, docType),
                      })}
                      onChange={() => toggle(company, docType)}
                      onKeyDown={(event) => moveFocus(event, row, column)}
                    />
                  </td>
                ))}
                <td className="docmatrix__saved">
                  {savedId === company.id ? t('docTypes.saved') : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* `<output>`, nem `<p role="status">`: ugyanaz a szerep, de az elem maga
          viszi — a Biome `useSemanticElements` szabálya ezt kéri, és az
          ügyfél-PWA nyugtája is így áll. */}
      <output className="hint">
        {state.status === 'saving' ? t('docTypes.saving') : t('docTypes.hint')}
      </output>
    </>
  );
}
